import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ProductRating from '~/shared/components/ProductRating';
import { PromoMessage } from '~/shared/components/PromoMessage';
import Text from '~/shared/components/Text';
import { promoPriceHiddenPromoTypes } from '~/shared/constants/products';
import useGshop from '~/shared/utils/useGshop';
import { ProductName, ProductPrice } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import ProductDetailMarketingCallout from '../ProductDetailMarketingCallout';

const showOutfitPieceCount = config.get('public.brand.showOutfitPieceCount');
const speedySignupLayoutEnhanced = config.get(
  'public.brand.speedySignupLayoutEnhanced'
);

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  ${!speedySignupLayoutEnhanced &&
  css`
    justify-content: space-between;
  `}

  ${({ $strikeThrough }) =>
    $strikeThrough &&
    css`
      text-decoration: line-through;
    `}
`;

const ProductNameTitle = styled(Text)`
  text-align: left;

  ${({ theme }) => theme.pdpStyles.productOverlay.productNameTitle}
`;

const ProductCalloutWrapper = styled.div`
  & + ${ProductNameTitle} {
    margin-top: 8px;
  }
`;

const promoPriceStyleOverride = css`
  font-weight: inherit;
  font-size: inherit;
  color: inherit;
`;

const DisplayField = styled(Text)`
  margin-left: 4px;
`;

const RatingAndMarketingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductOverlay = ({ product }) => {
  const itemLength = product.bundleComponentProducts?.length;
  const { promoCode, promoTypeId } = product.promos;
  const hasPromo = Boolean(promoCode) && typeof promoCode === 'string';
  const hiddenPromoPrice = promoPriceHiddenPromoTypes.has(promoTypeId);

  const { isGshopActive } = useGshop();

  const ProductCallout = (
    <ProductCalloutWrapper>
      <ProductDetailMarketingCallout product={product} />
    </ProductCalloutWrapper>
  );

  return (
    <>
      {!speedySignupLayoutEnhanced && ProductCallout}
      <ProductNameTitle variant="modalHeader">
        <ProductName />
        {showOutfitPieceCount && itemLength ? (
          <FormattedMessage
            id="speedy_signup.outfit_pieces"
            defaultMessage="{numberOfPieces}-Piece Outfit"
            values={{
              numberOfPieces: itemLength,
            }}
          />
        ) : null}
      </ProductNameTitle>
      <RatingAndMarketingWrapper>
        <ProductRating shouldPreventLinking />
        {speedySignupLayoutEnhanced && ProductCallout}
      </RatingAndMarketingWrapper>
      <PriceInfo>
        <Text variant="displayFieldLabel">
          <PromoMessage showPromoPrice={false} />
          {!hasPromo && (
            <FormattedMessage
              id="speedy_signup.vip_label"
              defaultMessage="VIP:"
            />
          )}
        </Text>
        {!hiddenPromoPrice && (
          <DisplayField variant="displayFieldValue">
            <ProductPrice
              showVipPrice={!hasPromo}
              showPromoPrice={hasPromo}
              data-autotag="pdp-vip-price"
              promoPriceStyle={promoPriceStyleOverride}
            />
          </DisplayField>
        )}
      </PriceInfo>
      <PriceInfo $strikeThrough>
        <Text variant="displayFieldLabel">
          {isGshopActive ? (
            <FormattedMessage
              id="speedy_signup.gshop_reg_label"
              defaultMessage="Today:"
            />
          ) : (
            <FormattedMessage
              id="speedy_signup.reg_label"
              defaultMessage="Retail:"
            />
          )}
        </Text>
        <DisplayField variant="displayFieldValue">
          <ProductPrice
            showRetailPrice={!isGshopActive}
            showVipPrice={isGshopActive}
            showPromoPrice={false}
          />
        </DisplayField>
      </PriceInfo>
    </>
  );
};

ProductOverlay.propTypes = {
  product: PropTypes.object,
};

export default ProductOverlay;
